import { ContractorTypeLabels, SirenApiResponse } from "@dune/types";
import { Card, ControlledInput, ControlledSelect } from "@dune/ui";
import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useMutateGetSocietyInfoFromSiren } from "../../../../../../api/queries/siren.queries";
import { useDebounce } from "../../../../../../hooks/useDebounce";

type Props = {
	isEditing: boolean;
};

export const ContractorDetails: React.FC<Props> = ({ isEditing }) => {
	const {
		control,
		setValue,
		setError,
		clearErrors,
		formState: { errors },
	} = useFormContext();
	const [sirenSearchStatus, setSirenSearchStatus] = useState<
		"idle" | "searching" | "found" | "error"
	>("idle");
	const [sirenErrorMessage, setSirenErrorMessage] = useState<string>("");

	const siren = useWatch({ control, name: "siren" });
	const debouncedSiren = useDebounce(siren, 1000);
	const { mutate: getSocietyInfo } = useMutateGetSocietyInfoFromSiren();

	useEffect(() => {
		if (isEditing && debouncedSiren && debouncedSiren.length >= 9) {
			setSirenSearchStatus("searching");
			setSirenErrorMessage("");
			clearErrors("siren");

			getSocietyInfo(debouncedSiren, {
				onSuccess: (data: SirenApiResponse) => {
					if (data?.denomination) {
						setValue("denomination", data.denomination);

						const formattedAddress = data.headOfficeAddress
							? (() => {
									const streetPart = [
										data.headOfficeAddress.streetNumber,
										data.headOfficeAddress.streetName,
									]
										.filter(Boolean)
										.join(" ");

									const locationPart = [
										data.headOfficeAddress.postalCode,
										data.headOfficeAddress.city,
									]
										.filter(Boolean)
										.join(" ");

									return [streetPart, locationPart].filter(Boolean).join(", ");
								})()
							: "";

						setValue("headquartersAddress", formattedAddress);
						setSirenSearchStatus("found");
						clearErrors("siren");
					} else {
						setSirenSearchStatus("error");
						setSirenErrorMessage("Aucune société trouvée pour ce SIREN");
						setError("siren", {
							type: "manual",
							message: "SIREN non trouvé",
						});
					}
				},
				onError: (_error) => {
					setSirenSearchStatus("error");
					setSirenErrorMessage("Erreur lors de la recherche du SIREN");
					setError("siren", {
						type: "manual",
						message: "SIREN invalide",
					});
				},
			});
		} else if (
			isEditing &&
			debouncedSiren &&
			debouncedSiren.length > 0 &&
			debouncedSiren.length < 9
		) {
			setSirenSearchStatus("error");
			setSirenErrorMessage("Le SIREN doit contenir 9 caractères");
			setError("siren", {
				type: "manual",
				message: "SIREN trop court",
			});
		} else if (isEditing) {
			setSirenSearchStatus("idle");
			setSirenErrorMessage("");
			clearErrors("siren");
		}
	}, [
		debouncedSiren,
		getSocietyInfo,
		setValue,
		setError,
		clearErrors,
		isEditing,
	]);

	const contractorTypeOptions = Object.entries(ContractorTypeLabels).map(
		([value, label]) => ({
			value,
			label,
		}),
	);

	return (
		<div className="py-4">
			<div className="flex flex-row gap-4">
				<div className="flex flex-col gap-4 pb-3 w-full">
					<div className="text-text-main-blue font-semibold">INFORMATIONS</div>
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
						<div className="flex flex-col gap-4">
							<div className="flex flex-row gap-4">
								<div className="flex flex-col items-start gap-3 w-1/2">
									<ControlledInput
										label="Raison sociale"
										control={control}
										name="denomination"
										placeholder={
											!isEditing
												? ""
												: sirenSearchStatus === "found"
													? ""
													: "Entrez un SIREN valide pour afficher la raison sociale"
										}
									/>
									{sirenSearchStatus === "found" && isEditing && (
										<div className="text-sm text-green-600">
											Informations mises à jour automatiquement
										</div>
									)}
								</div>
								<div className="flex flex-col items-start gap-3 w-1/2">
									<ControlledSelect
										label="Type"
										control={control}
										name="type"
										options={contractorTypeOptions}
										disabled={!isEditing}
									/>
								</div>
							</div>
							<div className="flex flex-row gap-4">
								<div className="flex flex-col items-start gap-3 w-1/2">
									<ControlledInput
										label="SIREN"
										control={control}
										name="siren"
										disabled={!isEditing}
										error={errors.siren?.message as string}
										maxLength={9}
									/>
									{sirenSearchStatus === "searching" && isEditing && (
										<div className="text-sm text-blue-600">
											Recherche en cours...
										</div>
									)}
									{sirenSearchStatus === "error" &&
										sirenErrorMessage &&
										isEditing && (
											<div className="text-sm text-red-600">
												{sirenErrorMessage}
											</div>
										)}
								</div>
								<div className="flex flex-col items-start gap-3 w-1/2">
									<ControlledInput
										label="Adresse"
										control={control}
										name="headquartersAddress"
										placeholder={
											!isEditing
												? ""
												: sirenSearchStatus === "found"
													? ""
													: "Entrez un SIREN valide pour afficher l'adresse"
										}
									/>
									{sirenSearchStatus === "found" && isEditing && (
										<div className="text-sm text-green-600">
											Adresse mise à jour automatiquement
										</div>
									)}
								</div>
							</div>
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
};
